// PDFReportGenerator.js
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export const generateRiskReport = async (riskResults, customerName, vulnerabilities) => {
  const doc = new jsPDF();
  
  // Colors
  const primaryColor = [102, 126, 234]; // #667eea
  const dangerColor = [220, 38, 38];    // #dc2626
  const warningColor = [234, 88, 12];   // #ea580c
  const textColor = [30, 41, 59];       // #1e293b
  
  let yPos = 20;
  
  // ========== HEADER ==========
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, 210, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('Quantitative Risk Analysis Report', 105, 20, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(customerName || 'Organization', 105, 30, { align: 'center' });
  
  yPos = 50;
  
  // ========== EXECUTIVE SUMMARY ==========
  doc.setTextColor(...textColor);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Executive Summary', 20, yPos);
  yPos += 10;
  
  // EAL Box
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(20, yPos, 170, 35, 3, 3, 'F');
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text('EXPECTED ANNUAL LOSS (EAL)', 105, yPos + 8, { align: 'center' });
  
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  const ealAmount = formatCurrency(riskResults.total_eal);
  doc.text(ealAmount, 105, yPos + 20, { align: 'center' });
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  const ciText = `95% Confidence Interval: [${formatCurrency(riskResults.confidence_intervals.lower)}, ${formatCurrency(riskResults.confidence_intervals.upper)}]`;
  doc.text(ciText, 105, yPos + 28, { align: 'center' });
  
  yPos += 45;
  
  // Report Date
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  const reportDate = new Date(riskResults.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  doc.text(`Report Generated: ${reportDate}`, 20, yPos);
  yPos += 15;
  
  // ========== HUB CRITICALITY ==========
  if (riskResults.top_risk_drivers && riskResults.top_risk_drivers.length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textColor);
    doc.text('Top Risk Assets (Hub Criticality)', 20, yPos);
    yPos += 5;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text('These assets contribute the most to your overall cyber risk exposure.', 20, yPos);
    yPos += 8;
    
    const hubData = riskResults.top_risk_drivers.map((asset, index) => [
      `#${index + 1}`,
      asset.asset,
      formatCurrency(asset.eal),
      `${(asset.score * 100).toFixed(1)}%`,
      `${((asset.eal / riskResults.total_eal) * 100).toFixed(1)}%`
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Rank', 'Asset', 'EAL Contribution', 'Criticality Score', '% of Total Risk']],
      body: hubData,
      theme: 'grid',
      headStyles: {
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 9,
        textColor: textColor
      },
      columnStyles: {
        0: { cellWidth: 15, halign: 'center' },
        1: { cellWidth: 70 },
        2: { cellWidth: 35, halign: 'right' },
        3: { cellWidth: 30, halign: 'center' },
        4: { cellWidth: 30, halign: 'center' }
      },
      margin: { left: 20, right: 20 }
    });
    
    yPos = doc.lastAutoTable.finalY + 15;
  }
  
  // ========== VULNERABILITY SUMMARY ==========
  if (riskResults.risk_by_category) {
    // Add new page if needed
    if (yPos > 220) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textColor);
    doc.text('Vulnerability Summary', 20, yPos);
    yPos += 10;
    
    const vulnData = Object.entries(riskResults.risk_by_category).map(([severity, count]) => {
      let color;
      switch(severity) {
        case 'critical': color = [220, 38, 38]; break;
        case 'high': color = [234, 88, 12]; break;
        case 'medium': color = [245, 158, 11]; break;
        case 'low': color = [132, 204, 22]; break;
        default: color = [107, 114, 128];
      }
      return [severity.toUpperCase(), count.toString(), color];
    });
    
    autoTable(doc, {
      startY: yPos,
      head: [['Severity', 'Count']],
      body: vulnData.map(row => [row[0], row[1]]),
      theme: 'grid',
      headStyles: {
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 10
      },
      columnStyles: {
        0: { cellWidth: 50, fontStyle: 'bold' },
        1: { cellWidth: 30, halign: 'center' }
      },
      didParseCell: function(data) {
        if (data.section === 'body' && data.column.index === 0) {
          const rowColor = vulnData[data.row.index][2];
          data.cell.styles.textColor = rowColor;
        }
      },
      margin: { left: 20 }
    });
    
    yPos = doc.lastAutoTable.finalY + 15;
  }
  
  // ========== TOP VULNERABILITIES ==========
  if (vulnerabilities && vulnerabilities.length > 0) {
    doc.addPage();
    yPos = 20;
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textColor);
    doc.text('Top 10 Critical Vulnerabilities', 20, yPos);
    yPos += 5;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text('Prioritize remediation of these high-severity vulnerabilities.', 20, yPos);
    yPos += 8;
    
    vulnerabilities.slice(0, 10).forEach((vuln, index) => {
      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      // Vulnerability box
      const severityColor = vuln.severity === 5 ? dangerColor : warningColor;
      
      doc.setDrawColor(...severityColor);
      doc.setLineWidth(0.5);
      doc.rect(20, yPos, 170, 35);
      
      // Severity badge
      doc.setFillColor(...severityColor);
      doc.roundedRect(22, yPos + 2, 20, 6, 1, 1, 'F');
      doc.setFontSize(7);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text(vuln.severity === 5 ? 'CRITICAL' : 'HIGH', 32, yPos + 6, { align: 'center' });
      
      // Title
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...textColor);
      const title = doc.splitTextToSize(vuln.title, 140);
      doc.text(title, 24, yPos + 12);
      
      // Meta info
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      let metaY = yPos + 18;
      if (title.length > 1) metaY += 4;
      
      doc.text(`Host: ${vuln.hostname || vuln.ip_address}`, 24, metaY);
      if (vuln.cve_id) {
        doc.text(`CVE: ${vuln.cve_id}`, 100, metaY);
      }
      
      // Solution (truncated)
      doc.setFontSize(8);
      doc.setTextColor(...textColor);
      const solution = doc.splitTextToSize(`Solution: ${vuln.solution}`, 160);
      doc.text(solution.slice(0, 2), 24, metaY + 5);
      
      yPos += 38;
    });
  }
  
  // ========== FOOTER ON ALL PAGES ==========
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(
      `Page ${i} of ${pageCount}`,
      105,
      287,
      { align: 'center' }
    );
    doc.text(
      'CISO Advisor - Confidential',
      20,
      287
    );
  }
  
  // Save the PDF
  const fileName = `Risk_Analysis_Report_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

